import { CircularProgress } from "./CircularProgress";

interface CompletedChallengeCardProps {
  title: string;
  stakeAmount: string;
  isWinner: boolean;
  payoutAmount?: number;
  daysCompleted: number;
  requiredDays: number;
  onClick?: () => void;
  isLoading?: boolean;
}

export function CompletedChallengeCard({
  title,
  stakeAmount,
  isWinner,
  payoutAmount = 0,
  daysCompleted,
  requiredDays,
  onClick,
  isLoading = false,
}: CompletedChallengeCardProps) {
  const progress = Math.min(100, Math.round((daysCompleted / requiredDays) * 100));

  return (
    <div className="@container">
      <div
        onClick={onClick}
        className={`flex flex-col items-stretch justify-start rounded-3xl bg-background-light dark:bg-background-dark shadow-md dark:shadow-sm dark:shadow-primary/20 ${
          onClick
            ? "cursor-pointer hover:shadow-lg dark:hover:shadow-primary/30 transition-shadow"
            : ""
        }`}
      >
        <div className="p-4 flex flex-col gap-4">
          {/* Header: Title, Stake, and Circular Progress */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <p className="text-slate-900 dark:text-white text-lg font-bold leading-tight tracking-[-0.015em]">
                  {title}
                </p>
                {/* Completed Badge */}
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  isWinner
                    ? "bg-primary/20 text-primary"
                    : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                }`}>
                  {isWinner ? "Won" : "Completed"}
                </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-base font-normal leading-normal">
                {isWinner
                  ? `You earned $${payoutAmount.toFixed(2)}`
                  : `Staked: ${stakeAmount}`
                }
              </p>
            </div>
            <CircularProgress percentage={progress} size={64} />
          </div>

          {/* Linear Progress Bar */}
          <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${isWinner ? "bg-primary" : "bg-slate-400 dark:bg-slate-500"}`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Footer: Stats and Action Button */}
          <div className="flex items-end gap-3 justify-between">
            <div className="flex flex-col gap-1">
              <p className="text-slate-500 dark:text-slate-400 text-sm font-normal leading-normal">
                {daysCompleted} of {requiredDays} days completed
              </p>
              <p className={`text-sm font-medium leading-normal ${
                isWinner ? "text-primary" : "text-slate-500 dark:text-slate-400"
              }`}>
                {isWinner ? "Challenge Won!" : "Better luck next time"}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!isLoading && onClick) {
                  onClick();
                }
              }}
              disabled={isLoading}
              className={`flex min-w-[84px] max-w-[480px] items-center justify-center overflow-hidden rounded-full h-10 px-4 text-sm font-medium leading-normal transition-opacity ${
                isLoading
                  ? "bg-primary/70 text-slate-900/70 cursor-not-allowed"
                  : isWinner
                    ? "bg-primary text-slate-900 cursor-pointer"
                    : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                  <span className="truncate">Loading...</span>
                </div>
              ) : (
                <span className="truncate">{isWinner ? "Claim Rewards" : "View Results"}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
